import { Headline, Text, TextLink } from '@oneaudi/unified-web-components';
import { ReactNode, useEffect } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { TechnicalDataEntryWrapper } from '~/app/pages/configurator/components/technical-data-dialog/components/technical-data-dialog-content/components/technical-data-sections/styles/technical-data-sections-style';
import { DataSection, DataSectionEntry } from '~/app/utils/data-sections';
import { useFootnotes } from '~/components/footnotes';
import { FootnoteReferenceNgc } from '~/components/footnotes/footnote-reference';

export function TechnicalDataSections({ sections, children }: { sections: DataSection[]; children?: ReactNode }) {
	const { reload } = useFootnotes();
	const { i18n } = useI18n();

	useEffect(() => {
		reload();
	}, [reload]);

	return (
		<ul className="flex flex-col gap-16">
			{sections.map((section) => {
				return (
					<div key={section.title}>
						{section.title === i18n('ngc.technicalData.consumptionAndEmission') && children}
						<TechnicalDataSection key={section.title} title={section.title} entries={section.entries} />
					</div>
				);
			})}
		</ul>
	);
}

function TechnicalDataSection({ title, entries }: DataSection) {
	const { config } = useMarketConfig();
	const { i18n } = useI18n();

	return (
		<li className="flex flex-col">
			<div className="flex w-full justify-between border-b-2 border-black pb-2">
				<Headline tag="label" variant={{ type: 'headline', order: '3', weight: 'normal' }}>
					{title}
				</Headline>
				{title.includes('WLTP') && config.wltpLink && (
					<TextLink size="md" variant={'standalone'} href={config.wltpLink} target="_blank">
						{i18n('ngc.technicalData.wltpLink')}
					</TextLink>
				)}
			</div>

			<ul className="flex flex-col">
				{entries.map((entry, index) => (
					<TechnicalDataEntry key={index} entry={entry} />
				))}
			</ul>
		</li>
	);
}

function TechnicalDataEntry({ entry }: { entry: DataSectionEntry }) {
	const { registerFootnote, getReference } = useFootnotes();

	const footnoteReference =
		entry.remarks &&
		entry.remarks[0] &&
		entry.remarks[0].value &&
		registerFootnote(entry.remarks[0].id, entry.remarks[0].value);

	return (
		<TechnicalDataEntryWrapper>
			<div className="technical-item">
				<Text variant={{ type: 'copy', order: '1', style: 'bold' }}>
					<span dangerouslySetInnerHTML={{ __html: entry.title }} />
					{footnoteReference && (
						<FootnoteReferenceNgc reference={getReference(entry.remarks?.at(0)?.id ?? '')} />
					)}
				</Text>
			</div>

			<div className="technical-item">
				<Text variant={{ type: 'copy', order: '1', style: 'normal' }}>
					<span dangerouslySetInnerHTML={{ __html: entry.value }} />
				</Text>
			</div>
		</TechnicalDataEntryWrapper>
	);
}
